import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, KeyRound, Send, Trash2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchEvent } from '@/store/eventSlice';
import { eventAccessService } from '@/services/eventAccessService';
import { EventAccess, EventAccessRole, EventAccessStatus } from '@/types';

const formatDate = (date?: Date | null) => {
  if (!date) return '—';
  return date.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const statusBadge = (status: EventAccessStatus) => {
  if (status === 'ACTIVE') {
    return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Activo</Badge>;
  }
  if (status === 'REVOKED') {
    return <Badge className="bg-red-100 text-red-800 border-red-200">Revocado</Badge>;
  }
  return <Badge className="bg-amber-100 text-amber-800 border-amber-200">Pendiente</Badge>;
};

const roleLabel = (role: EventAccessRole) =>
  role === 'EDITOR' ? 'Editor' : 'Solo lectura';

export const EventAccessPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const dispatch = useAppDispatch();
  const { selectedEvent, loading: eventLoading } = useAppSelector((s) => s.event);
  const { toast } = useToast();

  const [accesses, setAccesses] = useState<EventAccess[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<EventAccessRole>('READ_ONLY');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (eventId) dispatch(fetchEvent(eventId));
  }, [eventId, dispatch]);

  useEffect(() => {
    if (!eventId) return;
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const list = await eventAccessService.listForEvent(eventId);
        if (active) setAccesses(list);
      } catch (err) {
        console.error('Error loading event accesses:', err);
        if (active) {
          toast({
            title: 'Error',
            description: err instanceof Error ? err.message : 'No se pudieron cargar los accesos',
            variant: 'destructive',
          });
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [eventId, toast]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!eventId) return;
    const trimmed = email.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      await eventAccessService.invite({ eventoId: eventId, email: trimmed, role });
      // Refresh the list rather than optimistically push — the Edge Function
      // may have upserted an existing row.
      const list = await eventAccessService.listForEvent(eventId);
      setAccesses(list);
      setEmail('');
      setRole('READ_ONLY');
      toast({
        title: 'Invitación enviada',
        description: `Se envió un correo de invitación a ${trimmed}.`,
      });
    } catch (err) {
      console.error('Error inviting user:', err);
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'No se pudo enviar la invitación',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRoleChange = async (access: EventAccess, nextRole: EventAccessRole) => {
    if (nextRole === access.role) return;
    const prev = accesses;
    setAccesses((curr) => curr.map((a) => (a.id === access.id ? { ...a, role: nextRole } : a)));
    try {
      await eventAccessService.updateRole(access.id, nextRole);
      toast({ title: 'Rol actualizado' });
    } catch (err) {
      console.error('Error updating role:', err);
      setAccesses(prev);
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'No se pudo actualizar el rol',
        variant: 'destructive',
      });
    }
  };

  const handleRevoke = async (access: EventAccess) => {
    if (!confirm(`¿Revocar el acceso de ${access.invited_email}?`)) return;
    const prev = accesses;
    setAccesses((curr) => curr.filter((a) => a.id !== access.id));
    try {
      await eventAccessService.revoke(access.id);
      toast({ title: 'Acceso revocado' });
    } catch (err) {
      console.error('Error revoking access:', err);
      setAccesses(prev);
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'No se pudo revocar el acceso',
        variant: 'destructive',
      });
    }
  };

  if (loading || eventLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (!selectedEvent) {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold text-gray-900">Evento no encontrado</h2>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          <KeyRound className="w-7 h-7 text-brand-primary" />
          Accesos al evento
        </h1>
        <p className="text-gray-600 mt-1">
          {selectedEvent.nombre} · Invita a clientes o familiares a ver este evento.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Nueva invitación</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleInvite} className="grid grid-cols-1 md:grid-cols-[1fr_220px_auto] gap-3 items-end">
            <div className="space-y-2">
              <Label htmlFor="invite-email">Correo electrónico</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="cliente@ejemplo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="off"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-role">Permisos</Label>
              <Select value={role} onValueChange={(v) => setRole(v as EventAccessRole)}>
                <SelectTrigger id="invite-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="READ_ONLY">Solo lectura</SelectItem>
                  <SelectItem value="EDITOR">Editor (puede modificar)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" disabled={submitting || email.trim().length === 0}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Invitar
            </Button>
          </form>
          <p className="text-xs text-gray-500 mt-3">
            Recibirá un correo con un enlace para crear su contraseña. Si ya está registrado,
            le aparecerá el evento la próxima vez que inicie sesión.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Personas con acceso ({accesses.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {accesses.length === 0 ? (
            <p className="text-center text-gray-500 py-6">
              Aún no has invitado a nadie a este evento.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Correo</TableHead>
                  <TableHead className="w-44">Permisos</TableHead>
                  <TableHead className="w-28">Estado</TableHead>
                  <TableHead className="w-32">Invitado el</TableHead>
                  <TableHead className="w-32">Aceptó el</TableHead>
                  <TableHead className="w-20">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {accesses.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell className="font-medium break-all">{a.invited_email}</TableCell>
                    <TableCell>
                      <Select
                        value={a.role}
                        onValueChange={(v) => handleRoleChange(a, v as EventAccessRole)}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue>{roleLabel(a.role)}</SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="READ_ONLY">Solo lectura</SelectItem>
                          <SelectItem value="EDITOR">Editor</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>{statusBadge(a.status)}</TableCell>
                    <TableCell>{formatDate(a.created_at)}</TableCell>
                    <TableCell>{formatDate(a.accepted_at)}</TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRevoke(a)}
                        aria-label={`Revocar acceso de ${a.invited_email}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
